import {
  CaptureProfileClient,
  CaptureProfileDTO,
  FileResponse,
  InspectionClient,
  InspectionSetGroupDTO,
  ISOPrevExpInfo,
  PlateBarcode,
  PlateBasicInfoDTO,
  PlateClient,
  PlateInfoDTO,
  PlateIngredientAndDrawingRequestDTO,
  PlatesImagingTaskDTO,
  PrintBarcodeApiResponse,
  PrintBarcodeClient,
  ProteinFormulationDTO,
  ScoreRequestDTO,
  ScoresGroupsDTO,
  ScoresOfWellDropsDTO,
} from 'business/models/Models';
import ModelFactory from 'business/models/ModelFactory';
import BaseService from './BaseService';
import { ScoringWell } from 'business/models/CustomModels';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  DropImageParameters,
  PlateCompositeImage,
  PlateDrawingIdentifiers,
  PlateImageStructure,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { addImage } from 'utils/Global';
import { getCompositeImageKeysAsString } from 'redux/utils/experiment';
import { getPlainPlateDrawingIdentifiersList } from 'transformers/experiment';

export default class ScoreGroupService extends BaseService {
  async getScoreGroupsService(
    asJson = false,
    forceFetch = false,
  ): Promise<ScoresGroupsDTO[]> {
    const plate: PlateClient = ModelFactory(PlateClient, asJson, forceFetch);
    const root: ScoresGroupsDTO[] = await plate.getScoresGroups();

    return root;
  }

  async getProteinFormulationDetailsService(id): Promise<ProteinFormulationDTO> {
    const plate: PlateClient = ModelFactory(PlateClient);
    const pfDetails = await plate.getProteinFormulationDetails(id);
    return pfDetails;
  }

  async getPlateExperimentId(plateId: number): Promise<ISOPrevExpInfo> {
    const plate: PlateClient = ModelFactory(PlateClient);
    const expId = await plate.getPlateExperimentId(plateId);
    return expId;
  }

  async getPlateCompositeImageMemoryStream(
    plateId: number,
    dropNumber: number,
    regionName: string,
    cpId: number,
    inspectionId: number,
    regenerate: boolean = false,
  ): Promise<FileResponse> {
    const plate: PlateClient = ModelFactory(PlateClient, false, regenerate);
    return await plate.getCompositeImageMemoryStream(
      plateId,
      dropNumber,
      regionName,
      cpId,
      inspectionId,
    );
  }

  async regenerateCompositeImage(
    plateId: number,
    dropNumber: number,
    regionName: string,
    cpId: number,
    inspectionId: number,
  ): Promise<boolean> {
    const plate: PlateClient = ModelFactory(PlateClient, false, true);
    return await plate.regenerateCompositeImage(
      plateId,
      dropNumber,
      regionName,
      cpId,
      inspectionId,
    );
  }

  async getCaptureProfiles(): Promise<CaptureProfileDTO> {
    const client: CaptureProfileClient = ModelFactory(CaptureProfileClient);
    return client.getCaptureProfile();
  }

  async generateInspectionCSVFile(
    inspectionsList: InspectionSetGroupDTO[],
    expId: number,
    expName: string,
  ): Promise<FileResponse> {
    const client: InspectionClient = ModelFactory(InspectionClient);
    return client.generateInspectionSCVFile(inspectionsList, expId, expName);
  }

  async getScoreForScoringTool(): Promise<ScoresGroupsDTO[]> {
    const client: PlateClient = ModelFactory(PlateClient, false, true);
    return client.getScoreGroupsForScoringTool();
  }

  async getPlateInfoByExperimentId(expId: number): Promise<PlateBasicInfoDTO> {
    const client: PlateClient = ModelFactory(PlateClient);
    return client.getPlateInfoByExperimentId(expId);
  }

  async getExperimentPlatesInfo(treeNodeId: number): Promise<PlateInfoDTO[]> {
    const plate: PlateClient = ModelFactory(PlateClient);
    const result = await plate.getExperimentPlatesInfo(treeNodeId);
    return result;
  }

  async getScreenByExpId(expId: number): Promise<ExperimentDtoClient> {
    const plate: PlateClient = ModelFactory(PlateClient, false, true);
    const result = (await plate.getScreenByPlateId(expId)) as ExperimentDtoClient;
    return result;
  }

  async getPlateScoreBasedOnWellsByPlateId(
    plateId: number,
  ): Promise<ScoresOfWellDropsDTO[][]> {
    const plate: PlateClient = ModelFactory(PlateClient, false, true);
    const result = await plate.getPlateScoresBasedOnWells(plateId);
    return result;
  }

  async printScreenLotBarcode(screenLotId: number): Promise<PrintBarcodeApiResponse> {
    const client: PrintBarcodeClient = ModelFactory(PrintBarcodeClient, false, true);
    return client.performScreenLotBarcodPrinting(screenLotId);
  }

  async printPlateBarcode(plateId: number): Promise<PrintBarcodeApiResponse> {
    const client: PrintBarcodeClient = ModelFactory(PrintBarcodeClient, false, true);
    return client.performPlateBarcodPrinting(plateId);
  }

  async setPlatesImaging(plates: PlatesImagingTaskDTO[]): Promise<boolean> {
    const client: PlateClient = ModelFactory(PlateClient, false, true);
    return await client.setPlatesImaging(plates);
  }
  async setDropScores(scores: ScoringWell[]): Promise<number> {
    const scoresDto = scores.map((score) => {
      const scoreDto = new ScoreRequestDTO();
      scoreDto.batchID = score.imageBatchId;
      scoreDto.dropNumber = score.dropNumber;
      scoreDto.isDeleteScore = score.isDeleteScore;
      scoreDto.plateID = score.plateId;
      scoreDto.wellDropScoreId = score.wellDropScoreId;
      scoreDto.wellDropScoreGroupId = score.wellDropScoreGroupId;
      scoreDto.wellNumber = score.wellNumber;
      return scoreDto;
    });
    const client: PlateClient = ModelFactory(PlateClient);
    return client.setDropScores(scoresDto);
  }

  async getPlatesDrawingIdentifiers(
    plateDetails: PlateIngredientAndDrawingRequestDTO,
  ): Promise<PlateDrawingIdentifiers[]> {
    const client: PlateClient = ModelFactory(PlateClient, false, true);
    return getPlainPlateDrawingIdentifiersList(
      await client.getAllPlatesDrawingIdentifiers(plateDetails),
    );
  }

  async fetchAllCompositeImages(
    plateDto: PlateImageStructure[],
    dropNumber,
    regionName,
    cpID,
    inspectionSet,
    regenerate,
  ): Promise<PlateCompositeImage[]> {
    if (inspectionSet) {
      const promises = plateDto.map(async (plate) => {
        const plateInspection = inspectionSet.groupedInspections.find(
          (gi) => gi.plateId === plate.plateId,
        );

        if (plateInspection) {
          const key = getCompositeImageKeysAsString(
            { plateID: plate.plateId } as WellNavigationDetails,
            {
              dropNumber: dropNumber,
              regionName: regionName,
              cpId: cpID,
              inspection: inspectionSet,
            } as DropImageParameters,
          );
          if (regenerate) {
            await this.regenerateCompositeImage(
              plate.plateId,
              dropNumber,
              regionName,
              cpID,
              plateInspection.imageBatchId,
            );
          }
          try {
            const res = await this.getPlateCompositeImageMemoryStream(
              plate.plateId,
              dropNumber,
              regionName,
              cpID,
              plateInspection.imageBatchId,
              regenerate,
            );
            if (res?.data) {
              const blob = res.data;
              const imageURL = URL.createObjectURL(blob);
              const image = await addImage(imageURL);
              URL.revokeObjectURL(imageURL);
              return {
                plateId: plate.plateId,
                image: image,
                compositeImageKey: key,
              } as PlateCompositeImage;
            }
            return {
              plateId: plate.plateId,
              image: 'NoImage',
              compositeImageKey: key,
            } as PlateCompositeImage;
          } catch {
            return {
              plateId: plate.plateId,
              image: 'NoImage',
              compositeImageKey: key,
            } as PlateCompositeImage;
          }
        }
        return {
          plateId: plate.plateId,
          image: 'NoImage',
          compositeImageKey: null,
        } as PlateCompositeImage;
      });
      const results = await Promise.all(promises);
      return results.filter((x) => x);
    }
    return [];
  }
  async GetPlateDefaultBarcode(plateId: number): Promise<PlateBarcode> {
    const client: PlateClient = ModelFactory(PlateClient, false, true);
    const defBarcode = await client.getPlateDefaultBarcode(plateId);
    return defBarcode;
  }

  async EditPlateBarcode(plateId: number, barCode: string): Promise<boolean> {
    const client: PlateClient = ModelFactory(PlateClient, false, true);
    return client.editBarcode(plateId, barCode);
  }
}
